import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OpticalPathLayer } from "@/components/network/OpticalPath";
import { MapLockHandlers, MapLockOverlay } from "@/components/network/MapLock";
import { MyLocationLayer, MyLocationControl, useMyLocation } from "@/components/network/MyLocation";
import { FILL_COLOR, odpLevel, CABLE_COLOR, CABLE_WEIGHT, CABLE_JENIS_LABEL, fmtLen } from "@/lib/network";

const icon = (kind, color, label, selected) => {
  const sz = kind === "odc" ? 26 : 20;
  return L.divIcon({
    className: "",
    iconSize: [sz, sz],
    iconAnchor: [sz / 2, sz / 2],
    html: `<div style="position:relative;width:${sz}px;height:${sz}px">
      <div style="width:${sz}px;height:${sz}px;box-sizing:border-box;border-radius:${kind === "odc" ? "6px" : "50%"};background:${color};
        border:${selected ? 3 : 2}px solid ${selected ? "#111827" : "#fff"};box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>
      <span style="pointer-events:none;position:absolute;left:${sz + 4}px;top:50%;transform:translateY(-50%);white-space:nowrap;background:rgba(17,24,39,.85);
        color:#fff;font:600 10px/1.2 'JetBrains Mono',monospace;padding:2px 4px;border-radius:3px">${label}</span>
    </div>`,
  });
};

const OLT_COLOR = "#7C3AED";
export const REPAIR_COLOR = "#F59E0B";
const OLT_STATUS_COLOR = { AKTIF: OLT_COLOR, PERBAIKAN: REPAIR_COLOR, NONAKTIF: "#9CA3AF" };
const repairLine = { color: REPAIR_COLOR, weight: 3, dashArray: "8 6", opacity: 0.95 };
const oltIcon = (kode, status, selected) => { const c = OLT_STATUS_COLOR[status] || "#9CA3AF"; const label = status === "AKTIF" ? kode : `${kode} · ${status}`; return L.divIcon({
  className: "", iconSize: [30, 30], iconAnchor: [15, 15],
  html: `<div style="position:relative;width:30px;height:30px">
    <div style="width:22px;height:22px;margin:4px;transform:rotate(45deg);box-sizing:border-box;background:${c};
      border:${selected ? 3 : 2}px solid ${selected ? "#111827" : "#fff"};box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>
    <span style="pointer-events:none;position:absolute;left:34px;top:50%;transform:translateY(-50%);white-space:nowrap;background:${c};
      color:#fff;font:700 10px/1.2 'JetBrains Mono',monospace;padding:2px 4px;border-radius:3px">${label}</span>
  </div>`,
}); };

export const SPLIT_COLOR = "#0D9488";
const splitIcon = (label, off, selected) => L.divIcon({
  className: "", iconSize: [28, 28], iconAnchor: [14, 14],
  html: `<div style="position:relative;width:28px;height:28px">
    <div style="width:0;height:0;margin:3px 2px;border-left:12px solid transparent;border-right:12px solid transparent;
      border-bottom:20px solid ${off ? "#9CA3AF" : SPLIT_COLOR};filter:drop-shadow(0 0 ${selected ? 2 : 1}px ${selected ? "#111827" : "#fff"})"></div>
    <span style="pointer-events:none;position:absolute;left:30px;top:50%;transform:translateY(-50%);white-space:nowrap;background:${SPLIT_COLOR};
      color:#fff;font:700 10px/1.2 'JetBrains Mono',monospace;padding:2px 4px;border-radius:3px">${label}</span>
  </div>`,
});
const xy = (o) => [o.latitude, o.longitude];
// Dua ujung pada titik efektif yang sama (mis. sama-sama di dalam satu FDT/SERVER): garis tidak digambar.
export const sameXY = (a, b) => a.latitude === b.latitude && a.longitude === b.longitude;
export const OTB_COLOR = "#0E7490";
const otbIcon = (o, selected) => { const c = o.status === "AKTIF" ? OTB_COLOR : "#9CA3AF"; return L.divIcon({
  className: "", iconSize: [28, 22], iconAnchor: [14, 11],
  html: `<div style="position:relative;width:28px;height:22px" data-testid="otb-marker-${o.kode}">
    <div style="width:28px;height:22px;box-sizing:border-box;border-radius:3px;background:${c};border:${selected ? 3 : 2}px solid ${selected ? "#111827" : "#fff"};
      box-shadow:0 1px 4px rgba(0,0,0,.45);color:#fff;font:800 8px/18px sans-serif;text-align:center">OTB</div>
    <span style="pointer-events:none;position:absolute;left:32px;top:50%;transform:translateY(-50%);white-space:nowrap;background:rgba(17,24,39,.85);
      color:#fff;font:600 10px/1.2 'JetBrains Mono',monospace;padding:2px 4px;border-radius:3px">${o.kode}</span></div>`,
}); };

export const SERVER_COLOR = "#1E293B";
const serverIcon = (o, selected) => { const c = o.status === "AKTIF" ? SERVER_COLOR : "#9CA3AF"; return L.divIcon({
  className: "", iconSize: [30, 30], iconAnchor: [15, 15],
  html: `<div style="position:relative;width:30px;height:30px" data-testid="server-marker-${o.kode}">
    <div style="width:30px;height:30px;box-sizing:border-box;border-radius:8px;background:${c};border:${selected ? 3 : 2}px solid ${selected ? "#F59E0B" : "#fff"};
      outline:2px dashed ${c};outline-offset:2px;box-shadow:0 1px 4px rgba(0,0,0,.45);color:#fff;font:800 8px/26px sans-serif;text-align:center">SRV</div>
    <span style="pointer-events:none;position:absolute;left:36px;top:50%;transform:translateY(-50%);white-space:nowrap;background:${c};
      color:#fff;font:700 10px/1.2 'JetBrains Mono',monospace;padding:2px 4px;border-radius:3px">${o.kode} · ${o.member_count}</span></div>`,
}); };

export const FDT_COLOR = FILL_COLOR.ok;  // lingkaran biru seperti ODP, pembeda: cincin putus-putus + tulisan FDT
const fdtIcon = (o, selected) => { const c = o.status === "AKTIF" ? FDT_COLOR : FILL_COLOR.off; return L.divIcon({
  className: "", iconSize: [26, 26], iconAnchor: [13, 13],
  html: `<div style="position:relative;width:26px;height:26px" data-testid="fdt-marker-${o.kode}">
    <div style="width:26px;height:26px;box-sizing:border-box;border-radius:50%;background:${c};border:${selected ? 3 : 2}px solid ${selected ? "#111827" : "#fff"};
      outline:1.5px dashed ${c};outline-offset:2px;box-shadow:0 1px 4px rgba(0,0,0,.45);color:#fff;font:800 7.5px/1 sans-serif;letter-spacing:.2px;
      display:flex;align-items:center;justify-content:center">FDT</div>
    <span style="pointer-events:none;position:absolute;left:33px;top:50%;transform:translateY(-50%);white-space:nowrap;background:${c};
      color:#fff;font:700 10px/1.2 'JetBrains Mono',monospace;padding:2px 4px;border-radius:3px;box-shadow:0 0 0 1px rgba(255,255,255,.7)">${o.kode} · ${o.member_count}</span></div>`,
}); };

// Props marker: klik membuka detail; bila mode geser aktif marker dapat diseret dan disimpan saat dilepas.
const mk = (kind, o, onSelect, mv) => ({
  draggable: !!mv,
  eventHandlers: {
    click: () => onSelect({ kind, id: o.id }),
    dragend: (e) => {
      const m = e.target; const l = m.getLatLng();
      mv?.(kind, o, [+l.lat.toFixed(7), +l.lng.toFixed(7)], () => m.setLatLng([o.latitude, o.longitude]));
    },
  },
});

const repairNote = (o) => (o?.status === "PERBAIKAN" ? ` · OLT ${o.kode} PERBAIKAN (layanan mungkin terganggu)` : "");

function SplitLayer({ splitters, splitIn, splitOut, splitSrc, olts, odcById, odpById, showLinks, selected, onSelect, mv }) {
  const sp = Object.fromEntries(splitters.map((x) => [x.id, x]));
  const olt = Object.fromEntries(olts.map((o) => [o.id, o]));
  const inOf = Object.fromEntries(splitIn.map((i) => [i.splitter_id, i]));
  const line = { color: SPLIT_COLOR, weight: 2, dashArray: "4 6", opacity: 0.9 };
  return (
    <>
      {showLinks && splitIn.filter((i) => sp[i.splitter_id]?.has_coords && olt[i.olt_id]?.has_coords && !sameXY(olt[i.olt_id], sp[i.splitter_id])).map((i) => (
        <Polyline key={`si-${i.splitter_id}`} positions={[xy(olt[i.olt_id]), xy(sp[i.splitter_id])]} pathOptions={olt[i.olt_id].status === "PERBAIKAN" ? repairLine : line}>
          <Tooltip sticky>PON {i.nama_port} → {sp[i.splitter_id].kode} ({sp[i.splitter_id].rasio}){repairNote(olt[i.olt_id])}</Tooltip></Polyline>
      ))}
      {showLinks && splitSrc.map((i) => {
        const from = (i.source_kind === "odc" ? odcById : odpById)[i.source_id];
        const s = sp[i.splitter_id];
        if (!from?.has_coords || !s?.has_coords || sameXY(from, s)) return null;
        return (
          <Polyline key={`ss-${i.splitter_id}`} positions={[xy(from), xy(s)]} pathOptions={{ ...line, dashArray: "8 4" }}>
            <Tooltip sticky>{i.source_kind.toUpperCase()} {from.kode} → input {s.kode} ({s.rasio})</Tooltip></Polyline>
        );
      })}
      {showLinks && splitOut.filter((o) => o.target_kind !== "odc").map((o) => {
        const s = sp[o.splitter_id];
        const t = o.target_kind === "splitter" ? sp[o.target_id] : odpById[o.target_id];
        if (!s?.has_coords || !t?.has_coords || sameXY(s, t)) return null;
        return (
          <Polyline key={`so-${o.splitter_id}-${o.output_port}`} positions={[xy(s), xy(t)]} pathOptions={{ ...line, dashArray: "8 4" }}>
            <Tooltip sticky>{s.kode} ({s.rasio}) output {o.output_port} → {o.target_kind === "splitter" ? `input ${t.kode} (${t.rasio})` : `ODP ${t.kode}`}</Tooltip>
          </Polyline>
        );
      })}
      {showLinks && splitOut.filter((o) => o.target_kind === "odc" && odcById[o.odc_id]?.has_coords && sp[o.splitter_id]).map((o) => {
        const s = sp[o.splitter_id];
        const up = olt[inOf[o.splitter_id]?.olt_id];
        const src = s.has_coords ? s : up;
        if (!src?.has_coords || sameXY(src, odcById[o.odc_id])) return null;
        const base = s.has_coords ? line : { ...line, dashArray: "1 6", weight: 3 };
        return (
          <Polyline key={`so-${o.splitter_id}-${o.output_port}`} positions={[xy(src), xy(odcById[o.odc_id])]}
                    pathOptions={up?.status === "PERBAIKAN" ? { ...repairLine, dashArray: base.dashArray === "1 6" ? "1 6" : repairLine.dashArray } : base}>
            <Tooltip sticky>{s.kode} ({s.rasio}) output {o.output_port} → {odcById[o.odc_id].kode}{s.has_coords ? "" : " · splitter belum berkoordinat"}{repairNote(up)}</Tooltip>
          </Polyline>
        );
      })}
      {splitters.filter((x) => x.has_coords && !x.in_server).map((x) => (  // anggota SERVER tidak tampil sebagai titik
        <Marker key={x.id} position={xy(x)} icon={splitIcon(x.kode, x.status !== "AKTIF", selected?.id === x.id)} zIndexOffset={450}
                {...mk("splitter", x, onSelect, mv)}>
          <Tooltip>{x.nama} · {x.rasio} · {x.output_terpakai}/{x.jumlah_output} output terpakai</Tooltip>
        </Marker>
      ))}
    </>
  );
}

function OltLayer({ olts, links, odcById, showLinks, selected, onSelect, mv }) {
  const byId = Object.fromEntries(olts.map((o) => [o.id, o]));
  return (
    <>
      {showLinks && links.filter((l) => byId[l.olt_id]?.has_coords && odcById[l.odc_id]?.has_coords && !sameXY(byId[l.olt_id], odcById[l.odc_id])).map((l) => (
        <Polyline key={`ol-${l.pon_port_id}`} positions={[[byId[l.olt_id].latitude, byId[l.olt_id].longitude], [odcById[l.odc_id].latitude, odcById[l.odc_id].longitude]]}
                  pathOptions={byId[l.olt_id].status === "PERBAIKAN" ? repairLine : { color: OLT_COLOR, weight: 2, dashArray: "6 6", opacity: 0.85 }}>
          {byId[l.olt_id].status === "PERBAIKAN" && <Tooltip sticky>Relasi fisik tercatat{repairNote(byId[l.olt_id])}</Tooltip>}
        </Polyline>
      ))}
      {olts.filter((o) => o.has_coords && !o.in_server).map((o) => (
        <Marker key={o.id} position={[o.latitude, o.longitude]} icon={oltIcon(o.kode, o.status, selected?.id === o.id)} zIndexOffset={500}
                {...mk("olt", o, onSelect, mv)}>
          <Tooltip>{o.nama} · {o.teknologi} · {o.port_terpakai}/{o.port_count} PON terpakai · Status: {o.status}</Tooltip>
        </Marker>
      ))}
    </>
  );
}

// Pusat peta hanya berubah saat lokasi kantor dipilih/diganti, bukan saat data dimuat ulang.
function OfficeView({ viewKey, centers }) {
  const map = useMap();
  useEffect(() => {
    if (centers.length === 1) map.setView(centers[0], 15);
    else if (centers.length > 1) map.fitBounds(centers, { padding: [60, 60], maxZoom: 15 });
  }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function AutoResize({ dep }) {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 150); return () => clearTimeout(t); }, [dep, map]);
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

const pulseIcon = L.divIcon({
  className: "", iconSize: [40, 40], iconAnchor: [20, 20],
  html: '<div data-testid="network-focus-pulse" class="net-focus-ring" style="width:40px;height:40px;border-radius:50%;border:4px solid #F59E0B;background:rgba(245,158,11,.25);pointer-events:none"></div>',
});

function FocusView({ focus }) {
  const map = useMap();
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    if (!focus) { setPulse(null); return undefined; }
    if (focus.bounds) map.flyToBounds(focus.bounds, { padding: [40, 40], maxZoom: 18, duration: 0.8 });
    else map.flyTo(focus.latlng, Math.max(map.getZoom(), 18), { duration: 0.8 });
    setPulse(focus.latlng);
    const t = setTimeout(() => setPulse(null), 3500);
    return () => clearTimeout(t);
  }, [focus, map]);
  return pulse ? <Marker position={pulse} icon={pulseIcon} interactive={false} zIndexOffset={2000} /> : null;
}

function MapClicks({ onClick }) {
  useMapEvents({ click: (e) => onClick?.([+e.latlng.lat.toFixed(7), +e.latlng.lng.toFixed(7)]) });
  return null;
}

function CableLines({ cables, selected, onSelect }) {
  return cables.filter((c) => c.path?.length > 1).map((c) => {
    const sel = selected?.kind === "cable" && selected.id === c.id;
    return (
      <Polyline key={`c-${c.id}-${sel}`} positions={c.path} eventHandlers={{ click: () => onSelect({ kind: "cable", id: c.id }) }}
                pathOptions={{ color: sel ? "#111827" : CABLE_COLOR[c.status] || "#EA580C", weight: (CABLE_WEIGHT[c.jenis] || 3) + (sel ? 2 : 0),
                               opacity: 0.95, dashArray: c.status === "PERBAIKAN" ? "8 6" : null }}>
        <Tooltip sticky>{c.kode} · {CABLE_JENIS_LABEL[c.jenis]}{c.jumlah_core ? ` ${c.jumlah_core} core` : ""} · {fmtLen(c.panjang_m)}</Tooltip>
      </Polyline>
    );
  });
}

const bendIcon = L.divIcon({
  className: "", iconSize: [14, 14], iconAnchor: [7, 7],
  html: '<div data-testid="cable-bend-handle" title="Geser untuk merapikan rute" style="width:14px;height:14px;border-radius:50%;background:#fff;border:2px solid #0055FF;cursor:move;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>',
});

function DraftLine({ draft, onMoveBend }) {
  const [live, setLive] = useState(null);
  if (!draft?.from) return null;
  const points = draft.points.map((p, i) => (live?.i === i ? live.p : p));
  const pts = [[draft.from.latitude, draft.from.longitude], ...points, ...(draft.to ? [[draft.to.latitude, draft.to.longitude]] : [])];
  const pos = (e) => { const l = e.target.getLatLng(); return [+l.lat.toFixed(7), +l.lng.toFixed(7)]; };
  return (
    <>
      {pts.length > 1 && <Polyline positions={pts} pathOptions={{ color: "#0055FF", weight: 4, dashArray: "6 6" }} />}
      {draft.points.map((p, i) => (
        <Marker key={`${i}-${p[0]}-${p[1]}`} position={p} icon={bendIcon} draggable zIndexOffset={1000}
                eventHandlers={{ drag: (e) => setLive({ i, p: pos(e) }), dragend: (e) => { setLive(null); onMoveBend?.(i, pos(e)); } }} />
      ))}
    </>
  );
}

export function NetworkMapView({ odcs, odps, allOdc, centers, viewKey, selected, onSelect, showLinks, satellite,
                                 cables = [], showCables, draft, onMapClick, onMoveBend, olts = [], oltLinks = [], showOlt, focus,
                                 splitters = [], splitIn = [], splitOut = [], splitSrc = [], showSplit, onMoveNode, onPlace, opticalPath, otbs = [], servers = [], fdts = [],
                                 fullscreen = false, onFullscreen }) {
  const odcById = useMemo(() => Object.fromEntries(allOdc.map((o) => [o.id, o])), [allOdc]);
  const odpById = useMemo(() => Object.fromEntries(odps.map((o) => [o.id, o])), [odps]);
  const [locked, setLocked] = useState(true);
  const modeActive = !!(draft || onPlace || onMoveNode);  // Gambar Jalur / Geser: interaksi peta tetap aktif
  const isLocked = locked && !modeActive;
  const { me, locating, locate, clear: clearMe } = useMyLocation(odps);
  return (
    <div className={`relative h-full w-full ${isLocked ? "[&_.leaflet-container]:!cursor-pointer" : ""} ${draft || onPlace ? "[&_.leaflet-container]:!cursor-crosshair" : ""} [&_.leaflet-marker-draggable]:!cursor-move ${opticalPath?.nodes?.length > 1 ? "[&_.leaflet-overlay-pane_path:not(.optical-path)]:opacity-20" : ""}`} data-testid="network-map">
    <MapContainer center={centers[0]} zoom={15} style={{ height: "100%", width: "100%" }}
                  dragging={false} touchZoom={false} scrollWheelZoom={false} doubleClickZoom={false} boxZoom={false} keyboard={false}>
      <MapLockHandlers locked={isLocked} onUnlock={() => setLocked(false)} fullscreen={fullscreen} />
      {satellite ? (
        <TileLayer attribution="Tiles &copy; Esri" maxZoom={19}
                   url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
      ) : (
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      )}
      <OfficeView viewKey={viewKey} centers={centers} />
      <MyLocationLayer loc={me?.loc} nearest={me?.nearest} fitKey={me?.fitKey} />
      <AutoResize dep={selected ? selected.id : "none"} />
      <FocusView focus={focus} />
      {(draft || onPlace) && <MapClicks onClick={draft ? onMapClick : onPlace} />}
      {showCables && <CableLines cables={cables} selected={selected} onSelect={onSelect} />}
      <DraftLine draft={draft} onMoveBend={onMoveBend} />
      {showLinks && odps.filter((o) => !o.splitter_feed && o.has_coords && odcById[o.odc_id]?.has_coords && !sameXY(o, odcById[o.odc_id])).map((o) => (
        <Polyline key={`l-${o.id}`} positions={[[o.latitude, o.longitude], [odcById[o.odc_id].latitude, odcById[o.odc_id].longitude]]}
                  pathOptions={{ color: "#16A34A", weight: 1.5, dashArray: "4 6", opacity: 0.8 }} />
      ))}
      {showSplit && <SplitLayer splitters={splitters} splitIn={splitIn} splitOut={splitOut} splitSrc={splitSrc} olts={olts} odcById={odcById} odpById={odpById} showLinks={showLinks}
                                selected={selected} onSelect={onSelect} mv={onMoveNode} />}
      {showOlt && <OltLayer olts={olts} links={oltLinks} odcById={odcById} showLinks={showLinks} selected={selected} onSelect={onSelect} mv={onMoveNode} />}
      <OpticalPathLayer path={opticalPath} />
      {odcs.filter((o) => o.has_coords && !o.in_server).map((o) => (
        <Marker key={o.id} position={[o.latitude, o.longitude]} icon={icon("odc", o.status === "AKTIF" ? FILL_COLOR.odc : FILL_COLOR.off, o.kode, selected?.id === o.id)}
                {...mk("odc", o, onSelect, onMoveNode)}>
          <Tooltip>{o.nama} · {o.status}</Tooltip>
        </Marker>
      ))}
      {otbs.filter((o) => o.has_coords && !o.in_server).map((o) => (
        <Marker key={o.id} position={[o.latitude, o.longitude]} icon={otbIcon(o, selected?.id === o.id)} {...mk("otb", o, onSelect, onMoveNode)}>
          <Tooltip>{o.nama} · {o.jumlah_core} core · {o.status}</Tooltip>
        </Marker>
      ))}
      {servers.filter((o) => o.has_coords).map((o) => (
        <Marker key={o.id} position={[o.latitude, o.longitude]} icon={serverIcon(o, selected?.id === o.id)} zIndexOffset={300} {...mk("server", o, onSelect, onMoveNode)}>
          <Tooltip>SERVER {o.nama} · {o.member_count} anggota · {o.status}</Tooltip>
        </Marker>
      ))}
      {fdts.filter((o) => o.has_coords).map((o) => (
        <Marker key={o.id} position={[o.latitude, o.longitude]} icon={fdtIcon(o, selected?.id === o.id)} zIndexOffset={300} {...mk("fdt", o, onSelect, onMoveNode)}>
          <Tooltip>FDT {o.nama} · {o.member_count} anggota · {o.status}</Tooltip>
        </Marker>
      ))}
      {odps.filter((o) => o.has_coords && !o.in_server).map((o) => (
        <Marker key={o.id} position={[o.latitude, o.longitude]} icon={icon("odp", FILL_COLOR[odpLevel(o)], o.splitter_count ? `${o.kode} · ${o.splitter_count} SPL` : o.kode, selected?.id === o.id)}
                {...mk("odp", o, onSelect, onMoveNode)}>
          <Tooltip>{o.nama} · {o.port_terpakai}/{o.kapasitas_port} port{o.splitter_count ? ` · ${o.splitter_count} splitter di dalam kotak` : ""}</Tooltip>
        </Marker>
      ))}
    </MapContainer>
    {!modeActive && <MyLocationControl loc={me?.loc} nearest={me?.nearest} busy={locating} following={!!me} onLocate={locate} onClear={clearMe}
                                       onOpen={() => onSelect({ kind: "odp", id: me.nearest.odp.id })} />}
    <MapLockOverlay locked={isLocked} canLock={!modeActive} onLock={() => setLocked(true)}
                    fullscreen={fullscreen} onFullscreen={onFullscreen && ((v) => { if (v) setLocked(false); onFullscreen(v); })} />
    </div>
  );
}

export function MapLegend() {
  const rows = [["odc", FILL_COLOR.odc, "ODC"], ["odp", FILL_COLOR.ok, "ODP"], ["odp", FILL_COLOR.warn, "ODP Hampir Penuh (≥75%)"],
    ["odp", FILL_COLOR.full, "ODP Penuh"], ["odp", FILL_COLOR.off, "Tidak Aktif / Perbaikan"]];
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 rounded-lg border border-[#E5E7EB] bg-white p-3 text-xs" data-testid="network-map-legend">
      <div className="flex items-center gap-2"><span style={{ background: OLT_COLOR }} className="inline-block h-2.5 w-2.5 rotate-45" />OLT</div>
      <div className="flex items-center gap-2" data-testid="legend-server"><span style={{ background: SERVER_COLOR, outline: `1px dashed ${SERVER_COLOR}`, outlineOffset: 1 }} className="inline-block h-3 w-3 rounded-[3px]" />SERVER (pengelompokan virtual)</div>
      <div className="flex items-center gap-2" data-testid="legend-fdt"><span style={{ background: FDT_COLOR, outline: `1px dashed ${FDT_COLOR}`, outlineOffset: 1 }} className="inline-block h-3 w-3 rounded-full" />FDT (kontainer virtual ODC/ODP/Splitter/OTB)</div>
      <div className="flex items-center gap-2" data-testid="legend-otb"><span style={{ background: OTB_COLOR }} className="inline-block h-2.5 w-3.5 rounded-sm" />OTB</div>
      {rows.map(([k, c, l]) => (
        <div key={l} className="flex items-center gap-2">
          <span style={{ background: c, borderRadius: k === "odc" ? 3 : 999 }} className="inline-block h-3 w-3" />{l}
        </div>
      ))}
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-[3px]" style={{ borderColor: CABLE_COLOR.AKTIF }} />Jalur kabel aktif</div>
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-[3px] border-dashed" style={{ borderColor: CABLE_COLOR.PERBAIKAN }} />Kabel perbaikan</div>
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-[3px]" style={{ borderColor: CABLE_COLOR.NONAKTIF }} />Kabel tidak aktif</div>
      <div className="text-[#6B7280]">Tebal garis: Feeder &gt; Distribusi &gt; Drop</div>
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-2 border-dashed border-[#16A34A]" />Relasi induk (bukan jalur kabel)</div>
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-2 border-dashed" style={{ borderColor: OLT_COLOR }} />Relasi OLT → ODC</div>
      <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rotate-45" style={{ background: REPAIR_COLOR }} />OLT PERBAIKAN · <span className="inline-block h-2.5 w-2.5 rotate-45 bg-[#9CA3AF]" />OLT NONAKTIF</div>
      <div className="flex items-center gap-2" data-testid="legend-olt-repair-line"><span className="inline-block w-4 border-t-[3px] border-dashed" style={{ borderColor: REPAIR_COLOR }} />Relasi dari OLT PERBAIKAN (fisik tercatat, layanan mungkin terganggu; titik-titik bila splitter tanpa koordinat)</div>
      <div className="flex items-center gap-2"><span className="inline-block h-0 w-0 border-x-[6px] border-b-[10px] border-x-transparent" style={{ borderBottomColor: SPLIT_COLOR }} />Splitter</div>
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-2 border-dashed" style={{ borderColor: SPLIT_COLOR }} />PON → Splitter → ODC</div>
      <div className="flex items-center gap-2"><span className="inline-block w-3 border-t-2 border-dotted" style={{ borderColor: SPLIT_COLOR }} />Splitter tanpa koordinat (garis dari OLT)</div>
    </div>
  );
}
